import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateDisplayDetailedBreakupModal, updateSelectedRoomNumberForModify } from "../../../reducer/bookingReducer";
import { Modal, ConfigProvider, message } from "antd";
import { FormControl, MenuItem, Select } from "@mui/material";

const DetailedBreakupModal = () => {
  const dispatch = useDispatch();

  // Memoized selector to prevent unnecessary re-renders
  const bookingState = useSelector((store) => store.booking);
  
  // Get hotel details for room type information
  const { hotelDetails } = useSelector((store) => store.login);
  const roomTypes = hotelDetails?.roomTypes || [];
  
  // Get selectedRoomWTypeAndNum for direct room updates
  const { selectedRoomWTypeAndNum } = useSelector((store) => store.booking);
  
  const { displayDetailedBreakupModal, detailedPriceBreakup } = useMemo(() => ({
    displayDetailedBreakupModal: bookingState.displayDetaileBreakupModal,
    detailedPriceBreakup: bookingState.detailedPriceBreakup
  }), [bookingState.displayDetaileBreakupModal, bookingState.detailedPriceBreakup]);


  // Helper functions to make code DRY
  const getRoomType = (roomData) => {
    return roomTypes.find(rt => rt.name === roomData.name);
  };

  const getRoomCapacity = (roomType) => {
    return roomType?.maxGuestAllowed || roomType?.guestsAllowed || 4;
  };

  const generateRoomKey = (roomData, index) => {
    return `detailed-breakup-${roomData.name}-${roomData.roomNumber || index}`;
  };

  const getRoomDisplayName = (roomData) => {
    return roomData.roomNumber || '-';
  };

  const getRoomTypeDisplayName = (roomData) => {
    return roomData.name || '-';
  };

  const getCurrentAdults = (roomData) => {
    return roomData.adults !== undefined ? roomData.adults : (roomData.occupancy || 0);
  };

  const getCurrentChildren = (roomData) => {
    return roomData.children !== undefined ? roomData.children : 0;
  };

  const findSpecificRoom = (roomTypeName, roomData) => {
    const updatedSelectedRooms = JSON.parse(JSON.stringify(selectedRoomWTypeAndNum));
    const roomsOfType = updatedSelectedRooms[roomTypeName] || [];
    const specificRoom = roomsOfType.find(room => room.id === roomData.id);
    return { updatedSelectedRooms, specificRoom };
  };

  const handleCancelBtn = () =>
    dispatch(updateDisplayDetailedBreakupModal(false));

  // Direct room update functions - Updates specific individual rooms
  const handleAdultChange = (roomData, newAdults) => {
    const roomType = getRoomType(roomData);
    
    if (!roomType) {
      return;
    }
    
    const maxGuestsPerRoom = getRoomCapacity(roomType);
    const minAdults = 1; // At least 1 adult per room
    
    if (newAdults < minAdults) {
      message.warning(`At least ${minAdults} adult required per room`);
      return;
    }
    
    if (newAdults > maxGuestsPerRoom) {
      message.warning(`Adults cannot exceed room capacity of ${maxGuestsPerRoom}`);
      return;
    }
    
    // Update the specific room directly
    const { updatedSelectedRooms, specificRoom } = findSpecificRoom(roomData.name, roomData);
    
    if (specificRoom) {
      specificRoom.adults = newAdults;
      specificRoom.children = 0; // Reset children when adults change
      specificRoom.occupancy = newAdults; // Update total occupancy
      
      dispatch(updateSelectedRoomNumberForModify(updatedSelectedRooms));
    }
  };

  const handleChildrenChange = (roomData, newChildren) => {
    const roomType = getRoomType(roomData);
    
    if (!roomType) {
      return;
    }
    
    const maxGuestsPerRoom = getRoomCapacity(roomType);
    const currentAdults = getCurrentAdults(roomData);
    
    if (currentAdults + newChildren > maxGuestsPerRoom) {
      message.warning(`Total guests cannot exceed room capacity of ${maxGuestsPerRoom}`);
      return;
    }
    
    // Update the specific room directly
    const { updatedSelectedRooms, specificRoom } = findSpecificRoom(roomData.name, roomData);
    
    
    if (specificRoom) {
      specificRoom.children = newChildren;
      specificRoom.occupancy = currentAdults + newChildren; // Update total occupancy
      
      dispatch(updateSelectedRoomNumberForModify(updatedSelectedRooms));
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          margin: 0,
          padding: 0,
        },
      }}
    >
      <Modal
        title={null}
        open={displayDetailedBreakupModal}
        onCancel={() => handleCancelBtn()}
        okButtonProps={{ disabled: true }}
        cancelButtonProps={{ disabled: true }}
        footer={null}
        width={"45rem"}
        styles={{ body: { height: "400px" } }} // Updated to use styles instead of bodyStyle
        closeIcon={null}
      >
        <div className="h-[100%]  overflow-auto">
          <div
            className={` overflow-y-auto no-scrollbar   rounded-lg  mx-3    flex flex-col h-[100%]  no-scrollbar `}
          >
            <div
              className={`  
                    rounded-t-lg   cursor-default bg-[#FFF] border-b-[1px] 
                    border-b-[#00000040] flex justify-between items-center`}
            >
              {[
                { title: "Room Name", width: "20%" },
                { title: "Room Type", width: "20%" },
                { title: "Adults", width: "15%" },
                { title: "Children", width: "15%" },
                { title: "Price", width: "30%" },
              ]?.map((head, index) => {
                return (
                  <div
                    className={`w-[${head?.width}] p-5 text-[#222B45] text-[16px] font-outfit font-500 leading-[1.21875rem] whitespace-nowrap`}
                    key={index}
                  >
                    {head?.title}
                  </div>
                );
              })}
            </div>

            <div className="rounded-b-lg divide-y  divide-gray-300  flex flex-col   overflow-y-auto">
              {detailedPriceBreakup?.length === 0 ? (
                // Fixed: Changed from <tr>/<td> to <div> for proper DOM nesting since we're not using a table structure
                <div className="rounded-b-xl bg-[#FFF] flex justify-center items-center">
                  <div className="cursor-default px-6 py-4 text-[14px] text-[#F36960]">
                    No rooms selected.
                  </div>
                </div>
              ) : (
                detailedPriceBreakup?.map((roomData, index) => {
                  // Find room type for capacity calculation
                  const roomType = getRoomType(roomData);
                  const maxGuestsPerRoom = getRoomCapacity(roomType);
                  const currentAdults = getCurrentAdults(roomData);
                  const currentChildren = getCurrentChildren(roomData);
                  const remainingCapacityForChildren = maxGuestsPerRoom - currentAdults;
                  
                  return (
                    <div
                      className={` ${
                        detailedPriceBreakup?.length - 1 === index
                          ? "rounded-b-[10px]"
                          : ""
                      }  bg-[#FFF] flex justify-between items-center`}
                      key={generateRoomKey(roomData, index)}
                    >
                      <div
                        className="cursor-default  w-[20%]   whitespace-nowrap  px-6  py-4  dark:border-neutral-500 text-[#4D4D4D] font-outfit font-[400] 
                        text-[0.875rem] "
                      >
                        {getRoomDisplayName(roomData)}
                      </div>
                      <div
                        className="cursor-default  w-[20%]   px-6  py-4  dark:border-neutral-500 text-[#4D4D4D] font-outfit font-[400] 
                        text-[0.875rem] "
                      >
                        {getRoomTypeDisplayName(roomData)}
                      </div>
                      <div
                        className="w-[15%] px-6 py-4 flex justify-center items-center"
                      >
                        <FormControl
                          sx={{
                            width: "4rem",
                            color: "#0000004D",
                            "& .MuiSelect-icon": { color: "#2F80ED" },
                          }}
                          size="small"
                        >
                          <Select
                            key={generateRoomKey(roomData, index)}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                },
                              },
                            }}
                            sx={{
                              borderRadius: "6px",
                              color: "#808080",
                              fontSize: "0.75rem",
                              fontWeight: "400",
                              fontFamily: "Poppins",
                            }}
                            value={currentAdults}
                            onChange={(e) => handleAdultChange(roomData, e.target.value)}
                            style={{ width: 80 }}
                            size="small"
                          >
                            {Array.from({ length: maxGuestsPerRoom }, (_, i) => i + 1).map(
                              (i) => (
                                <MenuItem key={i} value={i} sx={{ fontSize: "15px" }}>
                                  {i}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </div>
                      <div
                        className="w-[15%] px-6 py-4 flex justify-center items-center"
                      >
                        <FormControl
                          sx={{
                            width: "4rem",
                            color: "#0000004D",
                            "& .MuiSelect-icon": { color: "#2F80ED" },
                          }}
                          size="small"
                        >
                          <Select
                            key={generateRoomKey(roomData, index)}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                },
                              },
                            }}
                            sx={{
                              borderRadius: "6px",
                              color: "#808080",
                              fontSize: "0.75rem",
                              fontWeight: "400",
                              fontFamily: "Poppins",
                            }}
                            value={currentChildren}
                            onChange={(e) => handleChildrenChange(roomData, e.target.value)}
                            style={{ width: 80 }}
                            size="small"
                          >
                            {Array.from({ length: Math.max(remainingCapacityForChildren + 1, 1) }, (_, i) => i).map(
                              (i) => (
                                <MenuItem key={i} value={i} sx={{ fontSize: "15px" }}>
                                  {i}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>
                      </div>
                      <div
                        className="cursor-default  w-[30%]   whitespace-nowrap  px-6  py-4  dark:border-neutral-500 text-[#4D4D4D] font-outfit font-[400] 
                        text-[0.875rem] "
                      >
                        {Boolean(roomData.rate)
                          ? roomData.rate?.toFixed(0)
                          : ""}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Modal>
    </ConfigProvider>
  );
};

export default DetailedBreakupModal;
